import { z } from 'zod';

import { PageQuerySchema, SearchQuerySchema } from '../shared/pagination';
import { ChannelBindingSummarySchema } from './channel-binding';

// A ChannelConnection is the self-contained account record: it carries the channel's
// credentials (encrypted at rest) so a single row fully describes how to talk to one
// external account. The secrets are write-only — accepted on create/update, encrypted,
// and never returned by any read model (only the `…Set` booleans are exposed).
//
// Not every credential applies to every channel: Instagram uses all three (verify token,
// app secret, access token), while Intercom's relay is authenticated by the platform-wide
// S2S token and only needs the access token for its note egress. Each stays optional rather
// than growing a per-channel variant of the record.
//
// `externalAccountId` is the account the connection speaks as or into, read per channel: the
// Instagram business account id the webhook payload carries, the Intercom workspace. Slack has
// two shapes. An agent's Slack app is keyed by its app id (`A…`), with the bot user id as the
// actor, the signing secret as the app secret and the bot token as the access token. The
// investigation announcement connection is keyed by the one channel id it posts into and holds
// only a bot token — no signing secret, so no inbound path can ever reach it.

export const CHANNELS = [
  'instagram',
  'facebook',
  'intercom',
  'slack',
  'sbc',
  'tiktok',
  'facebook_group',
  'quo',
  'whatsapp',
  'klaviyo_sms',
] as const;
export const ChannelSchema = z.enum(CHANNELS);
export type Channel = z.infer<typeof ChannelSchema>;

export const BROWSER_SESSION_STATUSES = ['ok', 'needs_login'] as const;
export const BrowserSessionStatusSchema = z.enum(BROWSER_SESSION_STATUSES);
export type BrowserSessionStatus = z.infer<typeof BrowserSessionStatusSchema>;

// Public shape of a connection: the persistent, non-secret fields plus the derived
// per-secret `…Set` flags. Secrets never appear here — reads only ever see whether each
// credential is configured. Server-derived, never in the write contract.
export const ChannelConnectionSchema = z.object({
  id: z.uuid(),
  channel: ChannelSchema,
  externalAccountId: z.string(), // the IG business account id the webhook payload carries
  // The identity this connection writes AS on the external side — the Intercom admin id
  // that authors the investigator's internal note. Null when the channel has no separate
  // actor (Instagram posts as the account itself). Not a secret, so it is read back.
  externalActorId: z.string().nullable(),
  handle: z.string(), // display label, e.g. @asksbc
  enabled: z.boolean(), // disabled connections are ignored by routing
  inboxEnabled: z.boolean(),
  verifyTokenSet: z.boolean(),
  appSecretSet: z.boolean(),
  accessTokenSet: z.boolean(),
  // A Slack app's OAuth client, which the per-person connect link needs. The id is not secret.
  oauthClientId: z.string().nullable(),
  oauthClientSecretSet: z.boolean(),
  tokenExpiresAt: z.iso.datetime().nullable(), // when the access token expires; null = unknown
  sessionStatus: BrowserSessionStatusSchema.nullable(),
  sessionCheckedAt: z.iso.datetime().nullable(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});
export type ChannelConnection = z.infer<typeof ChannelConnectionSchema>;

// --- Read models for the admin UI ---

// list row: connection (minus secrets) + how many bindings hang off it.
export const ChannelConnectionSummarySchema = ChannelConnectionSchema.extend({
  bindingCount: z.number().int().nonnegative(),
});
export type ChannelConnectionSummary = z.infer<typeof ChannelConnectionSummarySchema>;

// detail page: connection + its bindings (each with the resolved agent primitives).
export const ChannelConnectionDetailSchema = ChannelConnectionSchema.extend({
  bindings: z.array(ChannelBindingSummarySchema),
  // How many people have granted a Slack app their own token and not revoked it. 0 elsewhere.
  slackUserGrantCount: z.number().int().nonnegative(),
});
export type ChannelConnectionDetail = z.infer<typeof ChannelConnectionDetailSchema>;

// --- Write contracts ---

// The three Meta secrets, write-only. Optional on both create and update: an operator can
// wire the account first and fill credentials once the Meta app is ready (the `…Set` flags
// reflect what's configured). On create, an omitted secret is left unset; on update, an
// omitted secret is left unchanged. A provided secret is non-empty.
const SecretSchema = z.string().min(1);

const ChannelConnectionWriteShape = {
  channel: ChannelSchema,
  externalAccountId: z.string().min(1).max(120),
  externalActorId: z.string().max(120).nullable().default(null),
  handle: z.string().min(1).max(120),
  webhookVerifyToken: SecretSchema.optional(), // GET webhook handshake
  appSecret: SecretSchema.optional(), // X-Hub-Signature-256 HMAC
  accessToken: SecretSchema.optional(), // Graph API token for outbound calls
  // Omitted ⇒ unset (create) / unchanged (update); null clears it.
  oauthClientId: z.string().min(1).max(120).nullable().optional(),
  oauthClientSecret: SecretSchema.optional(),
  enabled: z.boolean().default(true),
  inboxEnabled: z.boolean().default(false),
};

export const CreateChannelConnectionSchema = z.object(ChannelConnectionWriteShape);
export type CreateChannelConnection = z.infer<typeof CreateChannelConnectionSchema>;

export const UpdateChannelConnectionSchema = z.object(ChannelConnectionWriteShape);
export type UpdateChannelConnection = z.infer<typeof UpdateChannelConnectionSchema>;

// list = paging + search on handle / external account id.
export const ListChannelConnectionsQuerySchema = PageQuerySchema.merge(SearchQuerySchema);
export type ListChannelConnectionsQuery = z.infer<typeof ListChannelConnectionsQuerySchema>;

export const ChannelConnectionPageSchema = z.object({
  items: z.array(ChannelConnectionSummarySchema),
  page: z.number().int(),
  pageSize: z.number().int(),
  total: z.number().int(),
});
export type ChannelConnectionPage = z.infer<typeof ChannelConnectionPageSchema>;

export const TikTokConnectStartSchema = z.object({ authorizationUrl: z.url() });
export type TikTokConnectStart = z.infer<typeof TikTokConnectStartSchema>;

export const TIKTOK_CONNECT_ERRORS = ['unavailable', 'denied', 'failed'] as const;
export type TikTokConnectError = (typeof TIKTOK_CONNECT_ERRORS)[number];
